import React from "react"
import { Box, Tooltip } from "@mui/material"
import WarningAmberIcon from "@mui/icons-material/Warning"
import { DataGrid } from "@mui/x-data-grid"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { ConvertDDToDM, normaliseLng } from "@/utils/geoFormat"
import { nowMs } from "@/utils/appNow"
import {
  collectHraWarningItems,
  collectJwlaWarningItems,
  collectCustomAreaWarningItems,
  collectEcaInfoItems,
  uncheckedLegInfo,
} from "@/utils/routeHelper"

dayjs.extend(utc)

// Wind/current: prefer interpolated scalar fields, fall back to U/V components.
function speedDirFromComponents(U, V) {
  if (U == null || V == null) return [undefined, undefined]
  let d = (270 - (Math.atan2(V, U) * 180) / Math.PI) % 360
  if (d < 0) d += 360
  return [Math.sqrt(U * U + V * V), d]
}

export function getWindSpeedDir(p) {
  const ws = p?.weather_snapshot
  if (ws?.wind_gfs_speed_knots_interp != null)
    return [ws.wind_gfs_speed_knots_interp, ws.wind_gfs_direction_deg_interp]
  return speedDirFromComponents(p?.u10, p?.v10)
}

export function getCurrentSpeedDir(p) {
  const ws = p?.weather_snapshot
  if (ws?.current_hycom_speed_knots_interp != null)
    return [ws.current_hycom_speed_knots_interp, ws.current_hycom_direction_deg_interp]
  return speedDirFromComponents(p?.ucur, p?.vcur)
}

export function formatValueDir(value, dir, decimals = 2) {
  if (value == null || !Number.isFinite(Number(value))) return ""
  const v = Number(value).toFixed(decimals)
  if (dir == null || !Number.isFinite(Number(dir))) return v
  return `${v} / ${Math.round(Number(dir))}°`
}

// Turn a waypoint's `properties.warnings` + `leg_data` into a human-readable list.
// Mirrors WaypointCardContent so the tooltip matches what the map popup shows.
export function collectWaypointWarnings(props) {
  const items = []
  const w = props?.warnings
  if (!w || typeof w !== "object") return items
  const rnw = props?.rnw_evaluation
  if (w.warning_segment_wind_speed_knots)
    items.push(`Wind speed: ${Number(w.warning_segment_wind_speed_knots).toFixed(1)} kn`)
  if (w.warning_segment_wave_height_meter)
    items.push(`Wave height: ${Number(w.warning_segment_wave_height_meter).toFixed(1)} m`)
  if (w.warning_segment_current_speed_knots)
    items.push(`Current speed: ${Number(w.warning_segment_current_speed_knots).toFixed(1)} kn`)
  if (w.warning_is_closed_viaarea === true)
    items.push("Route passes through a closed via area")
  if (w.warning_leg_usable === true) {
    const rule = props?.restrictions?.ww_restriction_text_check
    items.push("Leg not usable" + (rule ? `: ${rule}` : ""))
  }
  if (w.warning_is_free_usage === true) {
    const usage = props?.leg_data?.usage
    items.push(
      usage === 2 ? "One-way leg" : `Usage restriction${usage != null ? ` (${usage})` : ""}`,
    )
  }
  if (w.warning_vertical_clearance === true) {
    const vc = props?.leg_data?.vertical_clearance
    items.push(
      `Insufficient vertical clearance${vc != null ? `: ${Number(vc).toFixed(1)} m` : ""}`,
    )
  }
  if (w.warning_water_depth === true) {
    const navCat = rnw?.ww_nav_cat
    items.push(`Insufficient water depth${navCat != null ? ` (nav cat: ${navCat})` : ""}`)
  }
  items.push(...collectHraWarningItems(w))
  items.push(...collectJwlaWarningItems(w))
  items.push(...collectCustomAreaWarningItems(w))
  return items
}

export function collectWaypointInfos(props) {
  const items = []
  const rnw = props?.rnw_evaluation
  if (rnw && typeof rnw === "object") {
    if (rnw.ww_maxspeed_knots != null)
      items.push(`Max speed: ${Number(rnw.ww_maxspeed_knots).toFixed(1)} kn`)
    if (rnw.ww_minspeed_knots != null)
      items.push(`Min speed: ${Number(rnw.ww_minspeed_knots).toFixed(1)} kn`)
  }
  const uncheckedLeg = uncheckedLegInfo(props?.warnings)
  if (uncheckedLeg) items.push(uncheckedLeg)
  items.push(...collectEcaInfoItems(props?.warnings))
  return items
}

function timestampMs(ts) {
  if (!ts) return NaN
  // Numbers are UNIX seconds, everything else an ISO-like string.
  const d = typeof ts === "number" ? new Date(ts * 1000) : new Date(ts)
  return d.getTime()
}

function warningIcon(items, color) {
  return (
    <Tooltip
      title={
        <Box>
          {items.map((txt, i) => (
            <div key={i}>{txt}</div>
          ))}
        </Box>
      }
      placement="right"
      arrow
    >
      <WarningAmberIcon sx={{ fontSize: 14, color }} />
    </Tooltip>
  )
}

const COLUMNS = [
  {
    field: "number",
    headerName: "WP",
    description:
      "Waypoint number. Decimal suffix (e.g. 5.2) marks an intermediate weather/RNW point inserted between original waypoints.",
    width: 50,
    valueGetter: (value, row) => {
      const n = row.properties.number
      const ext = row.properties.extended_number
      return ext ? `${n}.${ext}` : String(n)
    },
  },
  {
    field: "info",
    headerName: "Info",
    description:
      "Optional waypoint name (port/landmark) plus warning and speed-limit indicators. Hover icons for details.",
    width: 70,
    valueGetter: (value, row) => row.properties.name ?? "",
    renderCell: (params) => {
      const p = params.row?.properties
      const warnings = collectWaypointWarnings(p)
      const infos = collectWaypointInfos(p)
      return (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
          {params.value && <span>{params.value}</span>}
          {warnings.length > 0 && warningIcon(warnings, "#d32f2f")}
          {infos.length > 0 && warningIcon(infos, "#ed6c02")}
        </Box>
      )
    },
  },
  {
    field: "timestamp",
    headerName: "Time (UTC)",
    description: "ETA at this waypoint in UTC (YYYY-MM-DD HH:mm).",
    width: 120,
    valueGetter: (value, row) => row.properties.timestamp,
    renderCell: (params) => {
      if (!params.value) return ""
      const d = dayjs.utc(params.value)
      return d.isValid() ? d.format("YYYY-MM-DD HH:mm") : ""
    },
  },
  {
    field: "sog",
    headerName: "SOG (kn)",
    description:
      "Speed over ground (knots). Comes from the pathfinder response, sensor report, or Albis report.",
    width: 80,
    valueGetter: (value, row) => {
      const p = row.properties
      const sog =
        p?.sog ?? p?.albis_sensors?.speedOverGround_kn ?? p?.albis_reports?.speedOverGround_kn
      return sog != null && Number.isFinite(Number(sog)) ? Number(sog).toFixed(1) : ""
    },
  },
  {
    field: "heading",
    headerName: "HDG (°)",
    description:
      "Sensor heading (reported) or course to the next waypoint (planned) in degrees (0°=N, 90°=E).",
    width: 75,
    valueGetter: (value, row) => {
      const p = row.properties
      const hdg = p?.heading ?? p?.albis_sensors?.headingTrue_deg ?? p?.course_bearing
      return hdg != null && Number.isFinite(Number(hdg)) ? `${Math.round(Number(hdg))}°` : ""
    },
  },
  {
    field: "lat",
    headerName: "Latitude",
    description: "Latitude in degrees + decimal minutes (WGS84). Suffix N = north, S = south.",
    width: 95,
    valueGetter: (value, row) => {
      const [lon, lat] = row.geometry.coordinates
      return ConvertDDToDM([normaliseLng(lon), lat])[0]
    },
  },
  {
    field: "lng",
    headerName: "Longitude",
    description: "Longitude in degrees + decimal minutes (WGS84). Suffix E = east, W = west.",
    width: 100,
    valueGetter: (value, row) => {
      const [lon, lat] = row.geometry.coordinates
      return ConvertDDToDM([normaliseLng(lon), lat])[1]
    },
  },
  {
    field: "legtype",
    headerName: "Leg",
    description:
      "Leg geometry type from this waypoint to the next. GC = great circle, RL = rhumbline.",
    width: 60,
    valueGetter: (value, row) => {
      const raw = row.properties.legtype ?? row.properties.legType
      if (raw === 1 || raw === "1") return "rhumbline"
      if (raw === 0 || raw === "0") return "greatcircle"
      return typeof raw === "string" ? raw.toLowerCase() : raw
    },
    renderCell: (params) => {
      const v = params.value
      if (!v) return ""
      const short = v === "greatcircle" ? "GC" : v === "rhumbline" ? "RL" : v
      const color = "#004f7d"
      return (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            height: 18,
            px: 0.75,
            fontSize: 12,
            fontWeight: 600,
            color,
            borderRadius: "10px",
            border: `1px solid ${color}55`,
            backgroundColor: `${color}11`,
          }}
        >
          {short}
        </Box>
      )
    },
  },
  {
    field: "wind",
    headerName: "Wind (kn / °)",
    description:
      "Wind speed (knots) and direction (°, meteorological: direction the wind is coming FROM, 0°=N).",
    width: 100,
    headerClassName: "col-separator-left",
    cellClassName: "col-separator-left",
    valueGetter: (value, row) => formatValueDir(...getWindSpeedDir(row.properties)),
  },
  {
    field: "wave",
    headerName: "Wave (m / °)",
    description:
      "Significant wave height (m) and mean wave direction (°, direction waves are coming FROM, 0°=N).",
    width: 100,
    valueGetter: (value, row) => {
      const p = row.properties
      const h = p?.weather_snapshot?.wave_nww3_significant_height_m_interp ?? p?.hssw
      const d = p?.weather_snapshot?.wave_nww3_mean_direction_deg_interp ?? p?.dmean
      return formatValueDir(h, d, 2)
    },
  },
  {
    field: "current",
    headerName: "Current (kn / °)",
    description:
      "Ocean current speed (knots) and set direction (°, direction current flows TOWARDS, 0°=N).",
    width: 110,
    valueGetter: (value, row) => formatValueDir(...getCurrentSpeedDir(row.properties)),
  },
].map((c) => ({ ...c, sortable: false, editable: false }))

const GRID_SX = {
  overscrollBehavior: "contain",
  fontSize: 12,
  "& .MuiDataGrid-columnHeaderTitle": { fontSize: 12, fontWeight: 600 },
  "& .MuiDataGrid-virtualScroller": { overscrollBehavior: "contain" },
  "& .col-separator-left": { borderLeft: "1px solid rgba(0, 0, 0, 0.2)" },
  "& .row-past": { backgroundColor: "rgba(0, 0, 0, 0.04)", color: "rgba(0, 0, 0, 0.6)" },
  "& .row-alert": { backgroundColor: "rgba(255, 0, 0, 0.12)" },
  "& .row-warn": { backgroundColor: "rgba(211, 47, 47, 0.08)" },
  "& .row-info": { backgroundColor: "rgba(237, 108, 2, 0.08)" },
}

// Read-only waypoint grid for one route or draft. Rows are tinted by
// passed time, wind/wave limit breaches and waypoint warnings/infos.
export default function RouteWaypointGrid({ voyagePoints, windLimit, waveLimit }) {
  const rows = React.useMemo(
    () =>
      (voyagePoints?.features ?? []).map((row, index) => ({
        ...row,
        properties: {
          ...row.properties,
          number: row.properties?.number ?? index + 1,
          extended_number: row.properties?.extended_number ?? 0,
        },
      })),
    [voyagePoints],
  )

  const rowClassName = React.useCallback(
    (params) => {
      const props = params.row?.properties || {}
      const tsMs = timestampMs(props.timestamp)
      const isPast = !Number.isNaN(tsMs) && tsMs < nowMs()

      const [windSpeed] = getWindSpeedDir(props)
      const waveHeight =
        props?.weather_snapshot?.wave_nww3_significant_height_m_interp ?? props?.hssw
      const isAlert =
        (windLimit != null && windSpeed != null && windSpeed > windLimit) ||
        (waveLimit != null && waveHeight != null && waveHeight > waveLimit)

      const hasWarn = collectWaypointWarnings(props).length > 0
      const hasInfo = !hasWarn && collectWaypointInfos(props).length > 0

      const classNames = []
      if (isPast) classNames.push("row-past")
      if (isAlert) classNames.push("row-alert")
      else if (hasWarn) classNames.push("row-warn")
      else if (hasInfo) classNames.push("row-info")
      return classNames.join(" ")
    },
    [windLimit, waveLimit],
  )

  return (
    <DataGrid
      density="compact"
      style={{ height: "100%", width: "100%", borderRadius: "0px" }}
      sx={GRID_SX}
      onWheelCapture={(e) => e.stopPropagation()}
      rows={rows}
      columns={COLUMNS}
      disableColumnMenu
      disableColumnFilter
      disableColumnSelector
      disableColumnSorting
      isCellEditable={() => false}
      getRowClassName={rowClassName}
      getRowId={(row) => {
        const num = row.properties?.number
        const ext = row.properties?.extended_number ?? 0
        if (num != null) return `${num}.${ext}`
        if (row.properties?.timestamp) return row.properties.timestamp
        if (row.geometry?.coordinates)
          return `${row.geometry.coordinates[0]}_${row.geometry.coordinates[1]}`
        return JSON.stringify(row)
      }}
    />
  )
}
